import React, { useState, useEffect } from "react";
import { TextField, InputAdornment, TextFieldProps } from "@mui/material";
import { END_ADORNMENT_LABEL_SX } from "./CurrencyTextField";

interface BasePercentageTextFieldProps extends Omit<TextFieldProps, "onChange" | "value" | "onBlur"> {
  max?: number;
  min?: number;
  decimalPlaces?: number;
}

// ברירת מחדל – ערך מספרי בלבד (שדה ריק = 0)
interface NumberPercentageTextFieldProps extends BasePercentageTextFieldProps {
  nullable?: false;
  value: number;
  onChange: (value: number) => void;
  onBlur?: (value: number) => void;
}

// nullable – שדה ריק = null ("לא הוגדר"), כך ש-0 נשאר ערך תקין
interface NullablePercentageTextFieldProps extends BasePercentageTextFieldProps {
  nullable: true;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
}

type PercentageTextFieldProps = NumberPercentageTextFieldProps | NullablePercentageTextFieldProps;

/**
 * TextField מותאם לאחוזים עם סימן % בקצה ורוחב מקסימלי של 100px
 */
const PercentageTextField: React.FC<PercentageTextFieldProps> = ({
  nullable,
  value,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  max = 100,
  min = 0,
  decimalPlaces = 1,
  ...props
}) => {
  // ערך ריק: null במצב nullable, אחרת 0
  const emptyValue: number | null = nullable ? null : 0;
  const onChange = onChangeProp as (value: number | null) => void;
  const onBlur = onBlurProp as ((value: number | null) => void) | undefined;
  const formatValue = (v: number | null): string => (v === null ? "" : v.toFixed(decimalPlaces));

  const [displayValue, setDisplayValue] = useState<string>(formatValue(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    // עדכן את displayValue רק כשהשדה לא בפוקוס
    if (!isFocused) {
      setDisplayValue(formatValue(value));
    }
  }, [value, decimalPlaces, isFocused]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // אפשר ספרות, נקודה עשרונית, ומינוס בתחילת המספר בלבד
    if (/^-?\d*\.?\d*$/.test(newValue) || newValue === "" || newValue === "-") {
      setDisplayValue(newValue);

      // עדכן את הערך רק אם זה מספר תקין
      if (newValue !== "" && newValue !== "." && newValue !== "-") {
        const numValue = parseFloat(newValue);
        if (!isNaN(numValue)) {
          // הגבלה לטווח
          const clampedValue = Math.max(min, Math.min(max, numValue));
          onChange(clampedValue);
        }
      } else if (newValue === "" || newValue === "-") {
        onChange(emptyValue);
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    e.target.select();
  };

  const handleBlur = () => {
    setIsFocused(false);

    let finalValue: number | null = 0;
    if (displayValue === "" || displayValue === "." || displayValue === "-") {
      finalValue = emptyValue;
      setDisplayValue(formatValue(emptyValue));
    } else {
      const numValue = parseFloat(displayValue);
      if (!isNaN(numValue)) {
        const clampedValue = Math.max(min, Math.min(max, numValue));
        setDisplayValue(clampedValue.toFixed(decimalPlaces));
        finalValue = clampedValue;
      }
    }
    onChange(finalValue);
    onBlur?.(finalValue);
  };

  return (
    <TextField
      {...props}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={{
        maxWidth: "100px",
        ...END_ADORNMENT_LABEL_SX,
        ...props.sx,
      }}
      InputProps={{
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
      }}
      inputProps={{
        style: { direction: "ltr", textAlign: "right" },
      }}
    />
  );
};

export default PercentageTextField;
